import { Link } from 'components/link';
import { EMPTY_CELL } from 'utils/empty-cell';

// Re-exported so callers rendering a LinkCell can import both from here.
export { EMPTY_CELL };

export interface LinkCellProps {
  /** The cell text; a number (e.g. a record id) is shown as-is. */
  value?: string | number | null;
  /** The link target. No href → the value renders as plain text. */
  href?: string | null;
  /** Placeholder when there is no value (default the em-dash). */
  empty?: string;
  /** Show the full text on hover, for cells that can cut it off. */
  showTitle?: boolean;
}

/**
 * A table cell whose text links to a record, opening in a new tab.
 *
 * Degrading is the point: a row often has the text but not the link (a nullable
 * href, or a snapshot captured before that href was recorded), and such a row
 * must still show its text rather than an empty cell or a dead anchor. So the
 * link renders only when BOTH value and href are present; value alone renders as
 * plain text; neither renders the empty indicator.
 *
 * Opens in a new tab via the shared Link, which bundles `target="_blank"` with
 * `rel="noopener noreferrer"` — a table cell is somewhere a user follows a
 * reference without wanting to lose the page they are reading.
 *
 * @param {LinkCellProps} props - The cell text, its link target, the empty placeholder, and the hover-title opt-in.
 * @return {JSX.Element} The linked text, plain text, or the empty indicator.
 */
export function LinkCell({
  value,
  href,
  empty = EMPTY_CELL,
  showTitle = false,
}: LinkCellProps) {
  // A whitespace-only value counts as no value.
  const text = String(value ?? '').trim();
  if (!text || !href) {
    return <>{text || empty}</>;
  }
  return (
    <Link to={href} title={showTitle ? text : undefined} openInNewTab>
      {text}
    </Link>
  );
}
